import type { MyViewableTargetsResponse } from '../api/permission.api';

export interface UserWithTeams {
  id: number;
  teams: Array<{ id: number }>;
}

/**
 * Resolve the set of user IDs the current user is allowed to see in timesheets.
 *
 * @returns null if no filtering needed (admin or canViewAll), otherwise an array of allowed user IDs.
 */
export function resolveAllowedUserIds(
  resp: MyViewableTargetsResponse,
  currentUserId: number | null,
  users: UserWithTeams[],
): number[] | null {
  if (resp.canViewAll) return null;

  const ids = new Set<number>();
  if (currentUserId) ids.add(currentUserId);

  for (const t of resp.targets) {
    if (t.targetType === 'user' && t.targetId) {
      ids.add(Number(t.targetId));
    } else if (t.targetType === 'team' && t.targetId) {
      const teamId = Number(t.targetId);
      for (const u of users) {
        if (u.teams.some((tm) => tm.id === teamId)) {
          ids.add(u.id);
        }
      }
    }
  }

  return Array.from(ids);
}

/**
 * Check if the viewable targets response includes any timesheet_approve permissions.
 */
export function hasApprovePermission(resp: MyViewableTargetsResponse): boolean {
  return resp.targets.some((t) => t.permission === 'timesheet_approve');
}

/**
 * Filter user options to only include allowed user IDs.
 * If allowedUserIds is null (admin), returns all options.
 * If allowedUserIds is undefined (not resolved yet), returns empty array.
 */
export function filterUserOptions<T extends { id: number }>(
  options: T[],
  allowedUserIds: number[] | null | undefined,
): T[] {
  if (allowedUserIds === undefined) return [];
  if (allowedUserIds === null) return options;
  const allowedSet = new Set(allowedUserIds);
  return options.filter((o) => allowedSet.has(o.id));
}

/**
 * Filter team options to only include teams that have at least one allowed member.
 * If allowedUserIds is null (admin), returns all options.
 * If allowedUserIds is undefined (not resolved yet), returns empty array.
 */
export function filterTeamOptions<T extends { id: number }>(
  teamOptions: T[],
  teamMemberMap: Map<number, Set<number>>,
  allowedUserIds: number[] | null | undefined,
): T[] {
  if (allowedUserIds === undefined) return [];
  if (allowedUserIds === null) return teamOptions;
  const allowedSet = new Set(allowedUserIds);
  return teamOptions.filter((t) => {
    const memberIds = teamMemberMap.get(t.id);
    if (!memberIds) return false;
    return Array.from(memberIds).some((id) => allowedSet.has(id));
  });
}

/**
 * Filter resolved team member IDs by allowed user IDs.
 * Used when a team is selected in the filter bar to restrict to permitted members.
 */
export function filterTeamMembers(
  memberIds: number[],
  allowedUserIds: number[] | null | undefined,
): number[] {
  if (allowedUserIds === null || allowedUserIds === undefined) return memberIds;
  const allowedSet = new Set(allowedUserIds);
  return memberIds.filter((id) => allowedSet.has(id));
}
